import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components/macro';
import arrowLarge from '../../assets/vectors/arrow-l.svg';
import arrowSmall from '../../assets/vectors/arrow-s.svg';

type DropdownSize = 's' | 'l';

const DropdownWrapper = styled.div<{ size: DropdownSize; enabled: boolean }>`
  position: relative;
  width: ${({ enabled, size }) => {
    if (size === 'l') {
      return '15rem';
    }
    if (enabled) {
      return '7.25rem';
    }
    return 'auto';
  }};
`;

const DropdownButton = styled.button<{ size: DropdownSize; enabled: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  border: none;
  border-radius: ${({ size }) => (size === 'l' ? '0.75rem' : '0.5rem')};
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.bg4};
  padding: ${({ size }) => (size === 'l' ? '0.95rem 0.75rem' : '0.44rem 0.7rem')};

  &:hover {
    cursor: ${({ enabled }) => (enabled ? 'pointer' : 'auto')};
  }
`;

const OptionsContainer = styled.div<{ size: DropdownSize; expanded: boolean }>`
  position: absolute;
  z-index: ${({ theme }) => theme.dropdownZ};
  top: calc(100% + 0.25rem);
  width: 100%;
  box-sizing: border-box;
  margin-top: 0.25rem;
  border-radius: ${({ size }) => (size === 'l' ? '0.75rem' : '0.5rem')};
  background: ${({ theme }) => theme.gray1};
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all ${({ theme }) => theme.fast} ease-out;
  max-height: ${({ expanded }) => (expanded ? '100vh' : '0rem')};
`;

const OptionWrapper = styled.button`
  width: 100%;
  border: none;
  background: ${({ theme }) => theme.bg4};
  margin: 0;
  padding: 0;

  &:not(:last-child) {
    margin-bottom: 0.066rem;
  }

  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.bg3};
  }
`;

const ArrowIcon = styled.img<{ size: DropdownSize; expanded: boolean }>`
  margin-left: auto;
  margin-right: ${({ size }) => (size === 'l' ? '0.44rem' : '0.066rem')};
  transition: all ${({ theme }) => theme.fast} ease-out;
  transform: rotate(${({ expanded }) => (expanded ? '180deg' : '0deg')});
`;

interface DropdownProps<T> {
  options: T[];
  selected?: T;
  renderOption: (option: T) => JSX.Element;
  renderSelected: (option: T) => JSX.Element;
  onChange: (option: T) => void;
  size: DropdownSize;
  hoverEnabled?: boolean;
  className?: string;
}

export function Dropdown<T extends { id: string }>({
  options,
  selected,
  renderOption,
  renderSelected,
  onChange,
  size,
  hoverEnabled = false,
  className,
}: DropdownProps<T>) {
  const [expanded, setExpanded] = useState(false);

  const toggle = useCallback(() => {
    if (options.length > 1) {
      setExpanded(!expanded);
    }
  }, [expanded, options]);

  const handleMouseEnter = useCallback(() => {
    if (hoverEnabled) {
      setExpanded(true);
    }
  }, [hoverEnabled]);

  const handleMouseLeave = useCallback(() => {
    if (hoverEnabled) {
      setExpanded(false);
    }
  }, [hoverEnabled]);

  const dropdownEnabled = useMemo(() => options.length > 1, [options]);

  const renderOptions = useMemo(
    () =>
      options
        .map((item) =>
          item.id !== selected?.id ? (
            <OptionWrapper
              onClick={() => {
                setExpanded(false);
                onChange(item);
              }}
              key={item.id}
            >
              {renderOption(item)}
            </OptionWrapper>
          ) : null,
        )
        .filter((item) => item),
    [options, renderOption, selected, onChange],
  );

  const renderSelectedOption = useMemo(
    () => selected && renderSelected(selected),
    [selected, renderSelected],
  );

  const arrowSrc = useMemo(() => (size === 'l' ? arrowLarge : arrowSmall), [size]);

  const handleBlur = () => {
    // Add timeout to avoid false blur
    setTimeout(() => {
      setExpanded(false);
    }, 100);
  };

  return (
    <DropdownWrapper
      size={size}
      onBlur={handleBlur}
      enabled={dropdownEnabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={className}
    >
      <DropdownButton onClick={toggle} size={size} enabled={dropdownEnabled}>
        {renderSelectedOption}
        {dropdownEnabled && <ArrowIcon size={size} src={arrowSrc} expanded={expanded} />}
      </DropdownButton>
      <OptionsContainer size={size} expanded={expanded}>
        {renderOptions}
      </OptionsContainer>
    </DropdownWrapper>
  );
}

Dropdown.defaultProps = {
  selected: undefined,
  hoverEnabled: false,
  className: '',
};
